import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Edit, Trash2, CheckCircle, Mail, X, MessageCircle, Phone } from 'lucide-react';
import { Button } from '../ui';
import apiService from '../../services/api';
import { buildSmsLink, buildWhatsAppLink, formatPhoneForDisplay } from '../../utils/phone';
import RosterCustomFieldCell, { RosterCustomFieldDefinition } from './RosterCustomFieldCell';

/**
 * Shifts-Only Roster (SOR) table.
 *
 * Rendered in place of the legacy FMR grid when `rosterMode === 'shifts_only'`.
 * Columns: [Select] # · Name · Playa Name · Status · Shifts · Skills · Actions
 *
 * Responsibilities:
 *   • Compute per-row Status (Active = has linked user account; Invited = none).
 *   • Enforce a 24-hour client-side cooldown display for the Remind button,
 *     based on `member.lastReminderAt` from the backend. Server independently
 *     enforces the same cooldown — client display is strictly advisory.
 *   • Remind is only offered for invited rows when `inviteRemindersEnabled` is
 *     true; signed-up members never get manual reminders. The button label stays
 *     "Remind" when disabled; the remaining time is only surfaced via the tooltip
 *     to keep the UI compact.
 *   • Support bulk selection and bulk-remind via the sticky action bar.
 *   • Provide a local Edit modal for changing Playa Name and toggling the
 *     Camp Lead role. The modal is the ONLY place Camp Lead can be changed
 *     for SOR members — it's been removed from the main grid to reduce
 *     visual noise. Camp Lead toggle is only enabled for Active members
 *     (requires a linked User account server-side).
 */

const COOLDOWN_MS = 24 * 60 * 60 * 1000;

export interface SorMemberRow {
  /** The roster member entry _id (typically the Member document id). */
  _id: string;
  /** Populated Member subdoc from the roster response. */
  member: any;
  /** Normalised user object (real User doc if linked, else a fallback built from Member). */
  user: any;
  isCampLead?: boolean;
  isEventsLead?: boolean;
  customFieldValues?: Record<string, any>;
  rosterStatus?: string;
  responseGroupExtraCount?: number;
  responseGroupOtherNames?: string[];
}

interface Props {
  rosterId: string | null;
  campId: string | null;
  members: SorMemberRow[];
  canEdit: boolean;
  canAssignCampLead: boolean;
  canAssignEventsLead?: boolean;
  canOpenContactDetails?: boolean;
  showContactSummary?: boolean;
  canSendReminders?: boolean;
  customFields?: RosterCustomFieldDefinition[];
  canEditCustomFields?: boolean;
  onCustomFieldSave?: (member: SorMemberRow, fieldKey: string, value: any) => Promise<void>;
  currentUserId?: string;
  limitRoleBadgesToCurrentUser?: boolean;
  onDelete: (member: SorMemberRow) => void;
  /** Called after any mutation (reminder, edit, camp-lead change) to let the parent refresh roster data. */
  onRefresh?: () => void;
  /** From GET /rosters/active: server allows invite nudges only when SOR_ROSTER_REMINDERS_ENABLED=true. */
  inviteRemindersEnabled?: boolean;
}

function formatCooldownRemaining(lastReminderAt: string | Date | null | undefined): string | null {
  if (!lastReminderAt) return null;
  const last = new Date(lastReminderAt).getTime();
  const elapsed = Date.now() - last;
  if (elapsed >= COOLDOWN_MS) return null;
  const remainingMs = COOLDOWN_MS - elapsed;
  const hours = Math.floor(remainingMs / (60 * 60 * 1000));
  const minutes = Math.floor((remainingMs % (60 * 60 * 1000)) / (60 * 1000));
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

function deriveStatus(member: SorMemberRow): 'active' | 'invited' {
  const memberDoc = member.member || {};
  const hasUserAccount = !!(memberDoc.user && (memberDoc.user._id || typeof memberDoc.user === 'string'));
  return hasUserAccount ? 'active' : 'invited';
}

function memberDisplayName(member: SorMemberRow): string {
  const u = member.user || {};
  const memberDoc = member.member || {};
  const fromUser = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  if (fromUser) return fromUser;
  if (memberDoc.name) return memberDoc.name;
  return 'Unknown';
}

function memberEmail(member: SorMemberRow): string {
  return String(member.user?.email || member.member?.email || '').trim();
}

function memberPhoneNumber(member: SorMemberRow): string {
  return String(member.user?.phoneNumber || member.member?.phoneNumber || member.member?.phone || '').trim();
}

function memberPhoneCountryCode(member: SorMemberRow): string {
  return String(member.user?.phoneCountryCode || member.member?.phoneCountryCode || '').trim();
}

function memberPlayaName(member: SorMemberRow): string {
  // User doc is authoritative once linked; Member.playaName is the pre-signup value.
  return (member.user?.playaName || member.member?.playaName || '').trim();
}

function memberSkills(member: SorMemberRow): string[] {
  const u = member.user || {};
  const memberDoc = member.member || {};
  const userSkills = Array.isArray(u.skills) ? u.skills : [];
  const memberSkills = Array.isArray(memberDoc.skills) ? memberDoc.skills : [];
  return userSkills.length >= memberSkills.length ? userSkills : memberSkills;
}

function memberShiftCount(member: SorMemberRow): number {
  return Number(member.member?.shiftSignupCount || 0);
}

function memberLastReminderAt(member: SorMemberRow): string | null {
  return member.member?.lastReminderAt || null;
}

function toIdString(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof (value as { toString?: () => string }).toString === 'function') {
    const stringValue = (value as { toString: () => string }).toString();
    return stringValue === '[object Object]' ? '' : stringValue;
  }
  return '';
}

function confirmLeadRoleReplacement(memberName: string, existingRoleName: string, requestedRoleName: string): boolean {
  return window.confirm(
    `${memberName} already has ${existingRoleName} access.\n\n` +
    `A roster member can only have one lead role. Replace ${existingRoleName} access with ${requestedRoleName} access?`
  );
}

// ─── Edit modal ─────────────────────────────────────────────────────────────
interface EditModalProps {
  open: boolean;
  member: SorMemberRow | null;
  rosterId: string | null;
  campId: string | null;
  canAssignCampLead: boolean;
  canAssignEventsLead: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const EditMemberModal: React.FC<EditModalProps> = ({
  open,
  member,
  rosterId,
  campId,
  canAssignCampLead,
  canAssignEventsLead,
  onClose,
  onSaved
}) => {
  const [playaName, setPlayaName] = useState('');
  const [isCampLead, setIsCampLead] = useState(false);
  const [initialIsCampLead, setInitialIsCampLead] = useState(false);
  const [isEventsLead, setIsEventsLead] = useState(false);
  const [initialIsEventsLead, setInitialIsEventsLead] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form state every time the modal opens for a new member.
  useEffect(() => {
    if (open && member) {
      setPlayaName(memberPlayaName(member));
      const startingLead = !!member.isCampLead;
      const startingEventsLead = !!member.isEventsLead;
      setIsCampLead(startingLead);
      setInitialIsCampLead(startingLead);
      setIsEventsLead(startingEventsLead);
      setInitialIsEventsLead(startingEventsLead);
      setError(null);
    }
  }, [open, member]);

  if (!open || !member) return null;

  const memberId = member._id;
  const status = deriveStatus(member);
  const isActive = status === 'active';
  const name = memberDisplayName(member);
  const initialPlayaName = memberPlayaName(member);
  const playaNameChanged = playaName.trim() !== initialPlayaName.trim();
  const campLeadChanged = isCampLead !== initialIsCampLead;
  const eventsLeadChanged = isEventsLead !== initialIsEventsLead;
  const hasChanges = playaNameChanged || campLeadChanged || eventsLeadChanged;

  const handleSave = async () => {
    if (!rosterId || !hasChanges) return;
    setSaving(true);
    setError(null);
    try {
      // 1) Persist the playa name update (if changed).
      if (playaNameChanged) {
        await apiService.updateRosterMember(rosterId, memberId, { playaName: playaName.trim() });
      }
      // 2) Persist the Camp Lead role change (if changed). The backend rejects
      //    this when the member has no linked User, so we only ever call it
      //    for Active members — the checkbox is disabled otherwise.
      const replacingEventsWithCamp = campLeadChanged && isCampLead && initialIsEventsLead;
      const replacingCampWithEvents = eventsLeadChanged && isEventsLead && initialIsCampLead;

      if (replacingEventsWithCamp) {
        await apiService.grantCampLeadRole(memberId, { replaceExistingRole: true });
      } else if (replacingCampWithEvents) {
        await apiService.grantEventsLeadRole(memberId, campId || undefined, { replaceExistingRole: true });
      } else {
        if (campLeadChanged) {
          if (isCampLead) await apiService.grantCampLeadRole(memberId);
          else await apiService.revokeCampLeadRole(memberId);
        }
        if (eventsLeadChanged) {
          if (isEventsLead) await apiService.grantEventsLeadRole(memberId, campId || undefined);
          else await apiService.revokeEventsLeadRole(memberId, campId || undefined);
        }
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCampLeadChange = (checked: boolean) => {
    if (checked && isEventsLead) {
      const confirmed = confirmLeadRoleReplacement(name, 'Events Lead', 'Camp Lead');
      if (!confirmed) return;
      setIsEventsLead(false);
    }
    setIsCampLead(checked);
  };

  const handleEventsLeadChange = (checked: boolean) => {
    if (checked && isCampLead) {
      const confirmed = confirmLeadRoleReplacement(name, 'Camp Lead', 'Events Lead');
      if (!confirmed) return;
      setIsCampLead(false);
    }
    setIsEventsLead(checked);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={() => !saving && onClose()}
    >
      <div
        className="bg-white rounded-lg max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Edit Member</h2>
              <p className="text-sm text-gray-500 mt-1">{name}</p>
            </div>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-600"
              onClick={onClose}
              disabled={saving}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Playa Name
              </label>
              <input
                type="text"
                value={playaName}
                onChange={(e) => setPlayaName(e.target.value)}
                disabled={saving}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            {canAssignCampLead && (
              <div className="border-t pt-4">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isCampLead}
                    onChange={(e) => handleCampLeadChange(e.target.checked)}
                    disabled={saving || !isActive}
                    className="mt-0.5 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">Camp Lead role</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {isActive
                        ? 'Grants unrestricted read/write access to the entire camp account.'
                        : "This member hasn't created an account yet. Camp Lead role becomes available after they follow their invite link and sign up."}
                    </div>
                  </div>
                </label>
              </div>
            )}

            {canAssignEventsLead && (
              <div className="border-t pt-4">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isEventsLead}
                    onChange={(e) => handleEventsLeadChange(e.target.checked)}
                    disabled={saving || !isActive}
                    className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">Events Lead role</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {isActive
                        ? 'Grants planning access for events, shifts, tasks, and surveys while keeping dues, applications, and roster admin controls hidden.'
                        : "This member hasn't created an account yet. Events Lead role becomes available after they follow their invite link and sign up."}
                    </div>
                  </div>
                </label>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
                {error}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={saving || !hasChanges}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main table ────────────────────────────────────────────────────────────
export const ShiftsOnlyRosterTable: React.FC<Props> = ({
  rosterId,
  campId,
  members,
  canEdit,
  canAssignCampLead,
  canAssignEventsLead = false,
  canOpenContactDetails = true,
  showContactSummary = false,
  canSendReminders: canSendRemindersProp,
  customFields = [],
  canEditCustomFields = false,
  onCustomFieldSave,
  currentUserId,
  limitRoleBadgesToCurrentUser = false,
  onDelete,
  onRefresh,
  inviteRemindersEnabled = false
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [reminderLoadingIds, setReminderLoadingIds] = useState<Set<string>>(new Set());
  const [bulkRemindLoading, setBulkRemindLoading] = useState(false);
  const [bulkResultBanner, setBulkResultBanner] = useState<string | null>(null);
  const [editingMember, setEditingMember] = useState<SorMemberRow | null>(null);

  // Local overlay of lastReminderAt updates so the cooldown UI reflects a just-sent
  // reminder immediately (server will return the same value on the next refresh).
  const [localReminderOverrides, setLocalReminderOverrides] = useState<Record<string, string>>({});
  const canSendReminders = canSendRemindersProp ?? canEdit;
  const showActionsColumn = canEdit || canSendReminders;

  const renderContactSummary = (member: SorMemberRow) => {
    if (!showContactSummary) return null;

    const email = memberEmail(member);
    const phoneNumber = memberPhoneNumber(member);
    const phoneCountryCode = memberPhoneCountryCode(member);
    const phoneDisplay = formatPhoneForDisplay(phoneNumber, phoneCountryCode);
    const smsHref = buildSmsLink(phoneNumber, phoneCountryCode);
    const whatsAppHref = buildWhatsAppLink(phoneNumber, phoneCountryCode);

    return (
      <div className="mt-1 space-y-1">
        <div className="text-xs text-gray-500">{email || 'No email'}</div>
        <div className="text-xs text-gray-500">{phoneDisplay || 'No phone'}</div>
        {phoneDisplay && (
          <div className="flex flex-wrap items-center gap-2">
            {smsHref && (
              <a
                href={smsHref}
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
                title={`Send SMS to ${phoneDisplay}`}
              >
                <Phone className="w-3 h-3" />
                SMS
              </a>
            )}
            {whatsAppHref && (
              <a
                href={whatsAppHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-800"
                title={`Send WhatsApp message to ${phoneDisplay}`}
              >
                <MessageCircle className="w-3 h-3" />
                WhatsApp
              </a>
            )}
          </div>
        )}
      </div>
    );
  };

  const rows = useMemo(() => {
    return members.map((m, index) => {
      const status = deriveStatus(m);
      const overriddenReminderAt = localReminderOverrides[m._id] || memberLastReminderAt(m);
      const cooldownText = formatCooldownRemaining(overriddenReminderAt);
      const canRemindInvited = canSendReminders && status === 'invited' && inviteRemindersEnabled && !cooldownText;
      return {
        member: m,
        index,
        status,
        name: memberDisplayName(m),
        playaName: memberPlayaName(m),
        skills: memberSkills(m),
        shiftCount: memberShiftCount(m),
        responseGroupExtraCount: Number(m.responseGroupExtraCount || 0),
        responseGroupOtherNames: Array.isArray(m.responseGroupOtherNames) ? m.responseGroupOtherNames : [],
        lastReminderAt: overriddenReminderAt,
        cooldownText,
        canRemind: canRemindInvited
      };
    });
  }, [members, localReminderOverrides, inviteRemindersEnabled, canSendReminders]);

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.member._id));
  const anySelected = canSendReminders && selectedIds.size > 0;

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.member._id));
    });
  }, [rows]);

  const toggleSelect = useCallback((memberId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }, []);

  const handleRemindOne = useCallback(
    async (memberId: string) => {
      if (!rosterId) return;
      setReminderLoadingIds((prev) => new Set(prev).add(memberId));
      try {
        const res = await apiService.remindRosterMember(rosterId, memberId);
        setLocalReminderOverrides((prev) => ({ ...prev, [memberId]: res.lastReminderAt }));
        onRefresh?.();
      } catch (err: any) {
        const status = err?.response?.status;
        const nextAllowedAt = err?.response?.data?.nextAllowedAt;
        if (status === 429 && nextAllowedAt) {
          // Server-side cooldown hit — mirror into local state.
          setLocalReminderOverrides((prev) => ({
            ...prev,
            [memberId]: new Date(new Date(nextAllowedAt).getTime() - COOLDOWN_MS).toISOString()
          }));
        } else {
          alert(err?.response?.data?.message || 'Failed to send reminder');
        }
      } finally {
        setReminderLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(memberId);
          return next;
        });
      }
    },
    [rosterId, onRefresh]
  );

  const handleBulkRemind = useCallback(async () => {
    if (!rosterId || selectedIds.size === 0) return;
    setBulkRemindLoading(true);
    setBulkResultBanner(null);
    try {
      const ids = Array.from(selectedIds);
      const res = await apiService.bulkRemindRosterMembers(rosterId, ids);
      const overrides: Record<string, string> = {};
      for (const r of res.results) {
        if (r.status === 'sent' && r.lastReminderAt) overrides[r.memberId] = r.lastReminderAt;
        if (r.status === 'cooldown' && r.nextAllowedAt) {
          overrides[r.memberId] = new Date(new Date(r.nextAllowedAt).getTime() - COOLDOWN_MS).toISOString();
        }
      }
      setLocalReminderOverrides((prev) => ({ ...prev, ...overrides }));
      setBulkResultBanner(res.message);
      setSelectedIds(new Set());
      onRefresh?.();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to send bulk reminders');
    } finally {
      setBulkRemindLoading(false);
    }
  }, [rosterId, selectedIds, onRefresh]);

  if (rows.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Mail className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        <p>
          {canEdit
            ? 'No members in this roster yet. Import a CSV or add members manually to get started.'
            : 'No members in this roster yet.'}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Sticky action bar — appears when anything is selected */}
      {anySelected && (
        <div className="sticky top-0 z-10 bg-orange-50 border-b border-orange-200 px-4 py-3 mb-2 flex items-center justify-between">
          <div className="text-sm text-orange-900">
            <strong>{selectedIds.size}</strong> member{selectedIds.size === 1 ? '' : 's'} selected
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkRemindLoading}
            >
              Clear
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="flex items-center gap-1"
              onClick={handleBulkRemind}
              disabled={bulkRemindLoading || !inviteRemindersEnabled}
              title={
                !inviteRemindersEnabled
                  ? 'SOR invite reminders are turned off. Set SOR_ROSTER_REMINDERS_ENABLED=true on the server to enable.'
                  : undefined
              }
            >
              <Bell className="w-4 h-4" />
              {bulkRemindLoading ? 'Sending…' : `Remind ${selectedIds.size} selected`}
            </Button>
          </div>
        </div>
      )}

      {bulkResultBanner && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-2 rounded mb-2 text-sm flex items-center justify-between">
          <span>
            <CheckCircle className="w-4 h-4 inline mr-2" />
            {bulkResultBanner}
          </span>
          <button
            className="text-green-700 hover:text-green-900 text-xs"
            onClick={() => setBulkResultBanner(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {canSendReminders && (
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    aria-label="Select all members"
                  />
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Playa Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shifts</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Skills</th>
              {customFields.map((field) => (
                <th
                  key={field.key}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[10rem]"
                >
                  {field.label}
                </th>
              ))}
              {showActionsColumn && (
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row) => {
              const { member, index, status, name, playaName, skills, shiftCount, cooldownText, canRemind, responseGroupExtraCount, responseGroupOtherNames } = row;
              const memberId = member._id;
              const realUserId = member.member?.user?._id || (typeof member.member?.user === 'string' ? member.member.user : null);
              const linkedUserId = toIdString(realUserId || member.user?._id);
              const canViewRoleBadges =
                !limitRoleBadgesToCurrentUser || (!!currentUserId && linkedUserId === currentUserId);
              const contact360Link = realUserId
                ? `/camp/${campId}/contacts/${realUserId}`
                : `/camp/${campId}/contacts/member/${memberId}`;
              const isReminderLoading = reminderLoadingIds.has(memberId);
              const isSelected = selectedIds.has(memberId);

              return (
                <tr key={memberId} className={isSelected ? 'bg-orange-50' : 'hover:bg-gray-50'}>
                  {canSendReminders && (
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(memberId)}
                        className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        aria-label={`Select ${name}`}
                      />
                    </td>
                  )}
                  <td className="px-4 py-4 text-sm text-gray-500 font-medium">{index + 1}</td>
                  <td className="px-4 py-4 text-sm text-gray-900">
                    {campId && canOpenContactDetails ? (
                      <Link to={contact360Link} className="text-orange-600 hover:text-orange-700 font-medium">
                        {name}
                      </Link>
                    ) : (
                      <span className="font-medium">{name}</span>
                    )}
                    {canViewRoleBadges && member.isCampLead && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-700 uppercase tracking-wide">
                        Lead
                      </span>
                    )}
                    {canViewRoleBadges && member.isEventsLead && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 uppercase tracking-wide">
                        Events
                      </span>
                    )}
                    {responseGroupExtraCount > 0 && (
                      <span
                        className="ml-2 inline-flex items-center rounded-full bg-blue-100 text-blue-800 text-[10px] px-2 py-0.5 font-semibold"
                        title={
                          responseGroupOtherNames.length > 0
                            ? `Also submitted for: ${responseGroupOtherNames.join(', ')}`
                            : 'Submitted as a grouped response'
                        }
                      >
                        +{responseGroupExtraCount}
                      </span>
                    )}
                    {renderContactSummary(member)}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-700">
                    {playaName || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-4 text-sm">
                    {status === 'active' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                        Invited
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm">
                    {shiftCount > 0 ? (
                      canOpenContactDetails ? (
                        <Link
                          to={`${contact360Link}#volunteer-shifts`}
                          className="text-orange-600 hover:text-orange-700 font-medium"
                        >
                          {shiftCount} signed up →
                        </Link>
                      ) : (
                        <span className="font-medium text-gray-700">{shiftCount} signed up</span>
                      )
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-700">
                    {skills.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {skills.slice(0, 4).map((s: string, i: number) => (
                          <span
                            key={`${memberId}-skill-${i}`}
                            className="inline-block px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700"
                          >
                            {s}
                          </span>
                        ))}
                        {skills.length > 4 && (
                          <span className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                            +{skills.length - 4}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  {customFields.map((field) => (
                    <td key={field.key} className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">
                      <RosterCustomFieldCell
                        field={field}
                        value={(member.customFieldValues || member.member?.customFieldValues || {})[field.key]}
                        canEdit={canEditCustomFields && Boolean(onCustomFieldSave)}
                        onSave={(value) => onCustomFieldSave
                          ? onCustomFieldSave(member, field.key, value)
                          : Promise.resolve()}
                      />
                    </td>
                  ))}
                  {showActionsColumn && (
                    <td className="px-4 py-4 text-sm whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2 flex-nowrap">
                        {canSendReminders && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="inline-flex items-center gap-1 shrink-0"
                            onClick={() => handleRemindOne(memberId)}
                            disabled={!canRemind || isReminderLoading}
                            title={
                              cooldownText
                                ? `Already reminded — next allowed in ${cooldownText}`
                                : status === 'active'
                                  ? 'Reminders are not sent after a member has signed up'
                                  : !inviteRemindersEnabled
                                    ? 'SOR invite reminders are turned off (set SOR_ROSTER_REMINDERS_ENABLED=true on the server to enable).'
                                    : 'Send a friendly reminder of the original invite'
                            }
                          >
                            <Bell className="w-3 h-3" />
                            {isReminderLoading ? 'Sending…' : 'Remind'}
                          </Button>
                        )}
                        {canEdit && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="inline-flex h-9 w-9 shrink-0 items-center justify-center p-0"
                              onClick={() => setEditingMember(member)}
                              title="Edit member"
                              aria-label={`Edit ${name}`}
                            >
                              <Edit className="w-4 h-4" aria-hidden="true" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="inline-flex h-9 w-9 shrink-0 items-center justify-center p-0 text-red-600 border-red-600 hover:bg-red-50"
                              onClick={() => onDelete(member)}
                              title="Delete member"
                              aria-label={`Delete ${name}`}
                            >
                              <Trash2 className="w-4 h-4" aria-hidden="true" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <EditMemberModal
        open={!!editingMember}
        member={editingMember}
        rosterId={rosterId}
        campId={campId}
        canAssignCampLead={canAssignCampLead}
        canAssignEventsLead={canAssignEventsLead}
        onClose={() => setEditingMember(null)}
        onSaved={() => {
          onRefresh?.();
        }}
      />
    </div>
  );
};

export default ShiftsOnlyRosterTable;
